import { Search, SlidersHorizontal, X } from 'lucide-react'
import { useId, useState } from 'react'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { useIsAtLeast } from '@/lib/use-viewport'
import { cn } from '@/lib/utils'

import { MODULE_LABELS } from './request-types'

/**
 * The inbox toolbar.
 *
 * Everything here narrows an array the client already holds. The endpoint takes no parameters at
 * all — the filtering that matters is the server's, and it has already happened — so these are
 * reading aids over a queue rather than a query, and they are labelled as such.
 *
 * `ANY` stands in for "no filter" because a select option cannot carry an empty value.
 */
const ANY = 'any'

export type DeadlineFilter = 'any' | 'today' | 'hour'

export interface BranchOption {
  id: string
  label: string
}

export interface InboxFiltersProps {
  searchText: string
  onSearchTextChange: (value: string) => void
  module: string | undefined
  onModuleChange: (module: string | undefined) => void
  /** Every module present in the queue, whether or not this build has a name for it. */
  moduleOptions: string[]
  branchId: string | undefined
  onBranchChange: (branchId: string | undefined) => void
  branchOptions: BranchOption[]
  deadline: DeadlineFilter
  onDeadlineChange: (deadline: DeadlineFilter) => void
  hasActiveFilters: boolean
  onClearFilters: () => void
}

export function InboxFilters({
  searchText,
  onSearchTextChange,
  module,
  onModuleChange,
  moduleOptions,
  branchId,
  onBranchChange,
  branchOptions,
  deadline,
  onDeadlineChange,
  hasActiveFilters,
  onClearFilters,
}: InboxFiltersProps) {
  const searchId = useId()
  const moduleId = useId()
  const branchFilterId = useId()
  const deadlineId = useId()
  const narrowingId = useId()

  const compact = !useIsAtLeast('sm')
  const narrowingCount = [module, branchId, deadline === 'any' ? undefined : deadline].filter(
    Boolean,
  ).length
  const [narrowingOpen, setNarrowingOpen] = useState(narrowingCount > 0)

  return (
    <div className="space-y-1.5 rounded-card border border-border-subtle bg-surface p-3">
      <div className="grid gap-3 sm:flex sm:flex-wrap sm:items-end">
        <div className="min-w-0 space-y-1.5 sm:min-w-64 sm:flex-1">
          <Label htmlFor={searchId}>Search the queue</Label>
          <div className="relative">
            <Search
              className="pointer-events-none absolute top-1/2 left-2.5 size-4 -translate-y-1/2 text-muted"
              aria-hidden
            />
            <Input
              id={searchId}
              value={searchText}
              // No autofocus, on any width. This screen is read before it is typed into: the first
              // thing a checker does is look at what is waiting, and a caret in a search box
              // invites narrowing a queue nobody has seen the size of yet.
              autoComplete="off"
              spellCheck={false}
              type="search"
              placeholder="Description, who submitted it, the operation, or a request id"
              className="pr-8 pl-8"
              onChange={(event) => onSearchTextChange(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Escape' && searchText !== '') {
                  event.preventDefault()
                  onSearchTextChange('')
                }
              }}
            />
            {searchText ? (
              <button
                type="button"
                onClick={() => onSearchTextChange('')}
                className="absolute top-1/2 right-1.5 flex size-6 -translate-y-1/2 items-center justify-center rounded-control text-muted transition-colors hover:bg-surface-hover hover:text-heading max-sm:size-11"
                aria-label="Clear the search"
                tabIndex={-1}
              >
                <X className="size-3.5" />
              </button>
            ) : null}
          </div>
        </div>

        {compact ? (
          <div className="flex items-center gap-2">
            <Button
              variant="secondary"
              className="flex-1"
              aria-expanded={narrowingOpen}
              aria-controls={narrowingId}
              onClick={() => setNarrowingOpen((open) => !open)}
            >
              <SlidersHorizontal />
              Filters
              {narrowingCount > 0 ? <Badge tone="brand">{narrowingCount}</Badge> : null}
            </Button>
            {hasActiveFilters ? (
              <Button variant="ghost" onClick={onClearFilters}>
                <X />
                Clear
              </Button>
            ) : null}
          </div>
        ) : null}

        <div
          id={narrowingId}
          className={cn('grid grid-cols-2 gap-3 sm:contents', compact && !narrowingOpen && 'hidden')}
        >
          <div className="space-y-1.5 sm:w-44">
            <Label htmlFor={moduleId}>Module</Label>
            <Select
              value={module ?? ANY}
              onValueChange={(value) => onModuleChange(value === ANY ? undefined : value)}
            >
              <SelectTrigger id={moduleId}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ANY}>Every module</SelectItem>
                {moduleOptions.map((value) => (
                  <SelectItem key={value} value={value}>
                    {MODULE_LABELS[value] ?? value}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5 sm:w-52">
            <Label htmlFor={branchFilterId}>Branch</Label>
            <Select
              value={branchId ?? ANY}
              onValueChange={(value) => onBranchChange(value === ANY ? undefined : value)}
            >
              <SelectTrigger id={branchFilterId}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ANY}>Every branch in the queue</SelectItem>
                {/*
                  Built from the branches present in the queue as well as the ones this user is
                  granted. For a checker with cross-branch reach the server applies no branch
                  predicate at all, so the queue routinely contains branches that are not in any
                  list the client holds — and a filter built only from that list would silently
                  offer no way to narrow to them.
                */}
                {branchOptions.map((option) => (
                  <SelectItem key={option.id} value={option.id}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="col-span-2 space-y-1.5 sm:w-44">
            <Label htmlFor={deadlineId}>Deadline</Label>
            <Select
              value={deadline}
              onValueChange={(value) => onDeadlineChange(value as DeadlineFilter)}
            >
              <SelectTrigger id={deadlineId}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="any">Any deadline</SelectItem>
                <SelectItem value="today">Lapses within six hours</SelectItem>
                <SelectItem value="hour">Lapses within the hour</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {!compact && hasActiveFilters ? (
          <Button variant="ghost" onClick={onClearFilters}>
            <X />
            Clear
          </Button>
        ) : null}
      </div>

      <p className="text-2xs text-muted">
        The queue is already filtered by the server to requests you may decide, in branches you may
        act for, excluding anything you raised yourself. These controls only narrow what is shown.
      </p>
    </div>
  )
}
